import { analyzeLegacyCronMigration } from './legacyCronMigration';

/** Cron form state used by builder, dialogs, and save validation. */
export interface CronScheduleSaveState {
  expression: string;
  explain: string;
  /** Live UTC expression from builder — used for next-run preview only. */
  previewExpression?: string;
  /** Bumps when builder on-type/value controls change (preview memo bust). */
  previewConfigKey?: string;
  /** Author IANA timezone — converted to UTC cron bytes on save. */
  timeZone: string;
  isDirty?: boolean;
  /** False when recurring validation fails after a user edit. */
  isValid?: boolean;
  canonicalExpression?: string;
}

export interface EditCronSaveOptions {
  /** User confirmed migrating a legacy stored cron on an otherwise untouched edit. */
  confirmLegacyMigration?: boolean;
}

/** Edit save: passthrough canonical bytes when the user did not change the builder. */
export function resolveEditCronSavePayload(
  cron: CronScheduleSaveState,
  storedExplain?: string,
  options?: EditCronSaveOptions
): { cronexpression: string; cronexplain: string } {
  if (!cron.isDirty) {
    if (options?.confirmLegacyMigration) {
      const analysis = analyzeLegacyCronMigration(
        cron.canonicalExpression ?? cron.expression,
        cron.timeZone
      );
      if (analysis?.needsMigration) {
        return {
          cronexpression: analysis.migratedExpression,
          cronexplain: analysis.migratedExplain,
        };
      }
    }

    return {
      cronexpression: cron.canonicalExpression ?? cron.expression,
      cronexplain: storedExplain ?? cron.explain,
    };
  }

  if (cron.isValid !== true) {
    throw new Error('Cannot save cron schedule until validation passes');
  }

  const canonical = cron.canonicalExpression ?? cron.expression;
  if (cron.expression.trim() === canonical.trim()) {
    return {
      cronexpression: canonical,
      cronexplain: storedExplain ?? cron.explain,
    };
  }

  return {
    cronexpression: cron.expression,
    cronexplain: cron.explain,
  };
}

/** Client-side gate for cron tab save (create or edit). */
export function isCronScheduleSubmittable(cron: CronScheduleSaveState): boolean {
  if (cron.isValid === false) {
    return false;
  }

  if (cron.isDirty) {
    return cron.isValid === true;
  }

  if (cron.isValid === true) {
    return true;
  }

  return !!cron.expression?.trim();
}
